"use client";

import { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { propertyTypes } from "@/data/main-data";
import { useLocale, useTranslations } from "next-intl";
import { MdNavigateNext } from "react-icons/md";
import { GrFormPrevious } from "react-icons/gr";
import { ChevronDown } from "lucide-react";

export default function PropertyTypes() {
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const swiperRef = useRef<any>(null);
  const t = useTranslations("Home");
  const locale = useLocale();

  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (swiper && prevRef.current && nextRef.current) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, []);

  const toggleOpen = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="py-12">
      <div className="sm:container px-4 md:px-0 lg:w-[85%] mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-main">
          {t("chooseProperty")}
        </h2>

        <div className="relative">
          {/* Navigation Buttons */}
          <button
            ref={prevRef}
            className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg border border-gray-200 rounded-full p-2 hover:bg-white transition"
            aria-label="Previous"
          >
            <GrFormPrevious className="text-xl" />
          </button>

          <button
            ref={nextRef}
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-lg border border-gray-200 rounded-full p-2 hover:bg-white transition"
            aria-label="Next"
          >
            <MdNavigateNext className="text-xl" />
          </button>

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onBeforeInit={(swiper: any) => {
              swiper.params.navigation = swiper.params.navigation || {};
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            modules={[Navigation]}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2.5 },
              768: { slidesPerView: 3.5 },
              1024: { slidesPerView: 5 },
            }}
            className="py-6"
          >
            {propertyTypes.map((type: any) => {
              const Icon = type.icon;
              const isOpen: any = openId === type.id;

              return (
                <SwiperSlide key={type.id}>
                  <div className="relative group border border-dashed border-[#BBD1F8] rounded-2xl p-5 sm:p-6 bg-white hover:border-main transition">
                    {/* Main Card */}
                    <div
                      className="flex flex-col items-center cursor-pointer"
                      onClick={() => toggleOpen(type.id)}
                    >
                      <div className="flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-full border border-main mb-3 sm:mb-4 transition group-hover:scale-105">
                        <Icon className="text-main" size={40} />
                      </div>

                      <div className="flex items-center gap-2">
                        <h3 className="text-center text-gray-700 font-medium text-sm sm:text-base">
                          {locale === "ar" ? type.label.ar : type.label.en}
                        </h3>

                        {type.children && (
                          <ChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        )}
                      </div>
                    </div>

                    {/* Children (Hover on desktop / Click on mobile) */}
                    {type.children && (
                      <div
                        className={`
                          mt-4 grid grid-cols-1 gap-2 overflow-hidden transition-all duration-300 ease-out
                          max-h-0 opacity-0
                          ${isOpen ? "max-h-48 opacity-100" : ""}
                          sm:group-hover:max-h-48 sm:group-hover:opacity-100
                        `}
                      >
                        {type.children.map((child: any) => {
                          const ChildIcon = child.icon;

                          return (
                            <div
                              key={child.id}
                              className="flex items-center gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer transition"
                            >
                              <ChildIcon className="text-main" size={18} />
                              <span className="text-sm text-gray-700">
                                {locale === "ar"
                                  ? child.label.ar
                                  : child.label.en}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
